/**
 * Assessment LocalStorage Management Utility
 * Handles saving and retrieving assessment attempts from localStorage
 * Includes cleanup, expiration, and data validation
 */

import { AssessmentAttempt } from '@/types/assessment.types';

const STORAGE_PREFIX = 'assessment_attempt_';
const EXPIRATION_HOURS = 24; // Attempts expire after 24 hours

interface StoredAttempt extends AssessmentAttempt {
  saved_at: string;
}

/**
 * Save assessment attempt to localStorage
 */
export function saveAssessmentAttempt(attempt: AssessmentAttempt): boolean {
  try {
    const storedAttempt: StoredAttempt = {
      ...attempt,
      saved_at: new Date().toISOString(),
    };
    const key = getStorageKey(attempt.assessment_id);
    localStorage.setItem(key, JSON.stringify(storedAttempt));
    return true;
  } catch (error) {
    console.error('Failed to save assessment attempt:', error);
    return false;
  }
}

/**
 * Load assessment attempt from localStorage
 */
export function loadAssessmentAttempt(assessmentId: string): AssessmentAttempt | null {
  try {
    const key = getStorageKey(assessmentId);
    const stored = localStorage.getItem(key);

    if (!stored) return null;

    const attempt: StoredAttempt = JSON.parse(stored);

    // Check if attempt has expired
    if (isAttemptExpired(attempt)) {
      removeAssessmentAttempt(assessmentId);
      return null;
    }

    // Remove saved_at before returning
    const { saved_at, ...assessmentAttempt } = attempt;
    return assessmentAttempt;
  } catch (error) {
    console.error('Failed to load assessment attempt:', error);
    return null;
  }
}

/**
 * Remove specific assessment attempt from localStorage
 */
export function removeAssessmentAttempt(assessmentId: string): boolean {
  try {
    const key = getStorageKey(assessmentId);
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Failed to remove assessment attempt:', error);
    return false;
  }
}

/**
 * Get all assessment attempts for a user
 */
export function getAllAssessmentAttempts(): StoredAttempt[] {
  try {
    const attempts: StoredAttempt[] = [];
    const keys = Object.keys(localStorage);

    for (const key of keys) {
      if (key.startsWith(STORAGE_PREFIX)) {
        const stored = localStorage.getItem(key);
        if (stored) {
          try {
            const attempt: StoredAttempt = JSON.parse(stored);
            attempts.push(attempt);
          } catch {
            // Skip invalid entries
            continue;
          }
        }
      }
    }

    return attempts;
  } catch (error) {
    console.error('Failed to get all assessment attempts:', error);
    return [];
  }
}

/**
 * Clean up expired assessment attempts
 */
export function cleanupExpiredAttempts(): number {
  try {
    const attempts = getAllAssessmentAttempts();
    let cleaned = 0;

    for (const attempt of attempts) {
      if (isAttemptExpired(attempt)) {
        removeAssessmentAttempt(attempt.assessment_id);
        cleaned++;
      }
    }

    return cleaned;
  } catch (error) {
    console.error('Failed to cleanup expired attempts:', error);
    return 0;
  }
}

/**
 * Clear all assessment attempts (for logout or reset)
 */
export function clearAllAssessmentAttempts(): boolean {
  try {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith(STORAGE_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
    return true;
  } catch (error) {
    console.error('Failed to clear all assessment attempts:', error);
    return false;
  }
}

/**
 * Check if user has a saved attempt for an assessment
 */
export function hasAssessmentAttempt(assessmentId: string): boolean {
  const attempt = loadAssessmentAttempt(assessmentId);
  return attempt !== null;
}

/**
 * Get time remaining for an assessment attempt
 */
export function getTimeRemaining(assessmentId: string): number | null {
  const attempt = loadAssessmentAttempt(assessmentId);
  return attempt?.time_remaining ?? null;
}

/**
 * Update answers for an existing attempt
 */
export function updateAssessmentAnswers(
  assessmentId: string,
  answers: Record<string, any>
): boolean {
  try {
    const attempt = loadAssessmentAttempt(assessmentId);
    if (!attempt) return false;

    const updatedAttempt: AssessmentAttempt = {
      ...attempt,
      answers,
    };

    return saveAssessmentAttempt(updatedAttempt);
  } catch (error) {
    console.error('Failed to update assessment answers:', error);
    return false;
  }
}

/**
 * Update time remaining for an existing attempt
 */
export function updateTimeRemaining(
  assessmentId: string,
  timeRemaining: number
): boolean {
  try {
    const attempt = loadAssessmentAttempt(assessmentId);
    if (!attempt) return false;

    const updatedAttempt: AssessmentAttempt = {
      ...attempt,
      time_remaining: timeRemaining,
    };

    return saveAssessmentAttempt(updatedAttempt);
  } catch (error) {
    console.error('Failed to update time remaining:', error);
    return false;
  }
}

/**
 * Get storage statistics
 */
export function getStorageStats(): {
  total_attempts: number;
  expired_attempts: number;
  storage_size_kb: number;
} {
  try {
    const attempts = getAllAssessmentAttempts();
    const expired = attempts.filter(isAttemptExpired).length;

    // Calculate approximate storage size
    let totalSize = 0;
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith(STORAGE_PREFIX)) {
        const value = localStorage.getItem(key);
        if (value) {
          totalSize += key.length + value.length;
        }
      }
    }

    return {
      total_attempts: attempts.length,
      expired_attempts: expired,
      storage_size_kb: Math.round((totalSize * 2) / 1024), // UTF-16 = 2 bytes per char
    };
  } catch (error) {
    console.error('Failed to get storage stats:', error);
    return {
      total_attempts: 0,
      expired_attempts: 0,
      storage_size_kb: 0,
    };
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

function getStorageKey(assessmentId: string): string {
  return `${STORAGE_PREFIX}${assessmentId}`;
}

function isAttemptExpired(attempt: StoredAttempt): boolean {
  const savedAt = new Date(attempt.saved_at);
  const now = new Date();
  const diffHours = (now.getTime() - savedAt.getTime()) / (1000 * 60 * 60);
  return diffHours > EXPIRATION_HOURS;
}

/**
 * Validate assessment attempt data structure
 */
export function validateAttempt(attempt: any): attempt is AssessmentAttempt {
  return (
    typeof attempt === 'object' &&
    attempt !== null &&
    typeof attempt.assessment_id === 'string' &&
    typeof attempt.started_at === 'string' &&
    typeof attempt.answers === 'object' &&
    Array.isArray(attempt.question_order)
  );
}

/**
 * Initialize storage cleanup on app start
 * Call this once when the app loads
 */
export function initializeAssessmentStorage(): void {
  // Clean up expired attempts
  const cleaned = cleanupExpiredAttempts();
  if (cleaned > 0) {
    console.log(`Cleaned up ${cleaned} expired assessment attempts`);
  }

  // Log storage stats in development
  if (process.env.NODE_ENV === 'development') {
    const stats = getStorageStats();
    console.log('Assessment storage stats:', stats);
  }
}
